import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { fmtUsd } from '../utils/fmtPrice';
import { EVM_WALLET_EXAMPLES } from '../utils/evmWalletMessages';
import {
  fetchBridgeConfig,
  getArbitrumUsdcBalanceReadOnly,
  runBridgeDeposit,
  validateBridgeDepositPreflight,
  formatBridgeDepositError,
} from '../utils/hlBridgeDeposit';
import { fetchSolDepositAddress } from '../utils/hlUnitDeposit';
import { moveUsdcSpotToPerpsWithBrowserWallet } from '../utils/hlUsdClassTransfer';
import './HlFundingGuide.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const HL_SPOT_URL = 'https://app.hyperliquid.xyz/trade/SOL/USDC';

function shortAddr(a) {
  if (!a || a.length < 10) return a || '—';
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

export default function HlFundingGuide({
  walletAddress,
  variant = 'default',
  embedded = false,
  onBalanceChange,
  defaultTab = 'checklist',
}) {
  const singleAsset = variant === 'singleAsset';
  const [tab, setTab] = useState(defaultTab);
  const [bridgeConfig, setBridgeConfig] = useState(null);
  const [hlUsdc, setHlUsdc] = useState(null);
  const [spotUsdc, setSpotUsdc] = useState(null);
  const [spotSol, setSpotSol] = useState(null);
  const [unifiedAccount, setUnifiedAccount] = useState(false);
  const [arbUsdc, setArbUsdc] = useState(null);
  const [amount, setAmount] = useState('50');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [txUrl, setTxUrl] = useState(null);
  const [solDeposit, setSolDeposit] = useState(null);
  const [solLoading, setSolLoading] = useState(false);
  const [solError, setSolError] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState('');

  const min = bridgeConfig?.minDepositUsdc ?? 5;
  const needsFirstDeposit = hlUsdc != null && hlUsdc < min;
  const accountActive = hlUsdc != null && hlUsdc >= min;

  const loadHlBalance = useCallback(async () => {
    if (!walletAddress) return;
    try {
      const { data } = await axios.get(`${API_BASE}/hyperliquid/hl-usdc-balance`, {
        headers: authHeaders(),
        params: { address: walletAddress },
      });
      setHlUsdc(data.tradableUsdc ?? data.perpsUsdc ?? data.withdrawableUsdc);
      setSpotUsdc(data.spotUsdc ?? null);
      setSpotSol(data.spotSol ?? null);
      setUnifiedAccount(!!data.unifiedAccount);
    } catch {
      setHlUsdc(null);
      setSpotUsdc(null);
      setSpotSol(null);
    }
  }, [walletAddress]);

  const loadArbBalance = useCallback(async () => {
    if (!walletAddress || !bridgeConfig) return;
    try {
      const bal = await getArbitrumUsdcBalanceReadOnly(walletAddress, bridgeConfig);
      setArbUsdc(bal);
    } catch {
      setArbUsdc(null);
    }
  }, [walletAddress, bridgeConfig]);

  const loadSolAddress = useCallback(async () => {
    if (!walletAddress) return;
    setSolLoading(true);
    setSolError(null);
    try {
      const data = await fetchSolDepositAddress(API_BASE, walletAddress, authHeaders);
      setSolDeposit(data);
    } catch (e) {
      setSolDeposit(null);
      setSolError(e.response?.data?.error || e.message);
    } finally {
      setSolLoading(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    fetchBridgeConfig(API_BASE).then(setBridgeConfig).catch(() => setBridgeConfig(null));
  }, []);

  useEffect(() => {
    loadHlBalance();
    loadArbBalance();
  }, [loadHlBalance, loadArbBalance]);

  useEffect(() => {
    if (tab === 'sol' && walletAddress && !solDeposit && !solLoading) {
      loadSolAddress();
    }
  }, [tab, walletAddress, solDeposit, solLoading, loadSolAddress]);

  const pollHlCredit = async (baseline) => {
    for (let i = 0; i < 24; i += 1) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const { data } = await axios.get(`${API_BASE}/hyperliquid/hl-usdc-balance`, {
          headers: authHeaders(),
          params: { address: walletAddress },
        });
        const next = data.withdrawableUsdc;
        setHlUsdc(next);
        if (next != null && baseline != null && next > baseline + 0.01) {
          setSuccess(`Credited on Hyperliquid — withdrawable USDC is now ${fmtUsd(next)}.`);
          setStep(null);
          onBalanceChange?.(next);
          return;
        }
      } catch {
        /* keep polling */
      }
    }
    setSuccess('Deposit sent. Hyperliquid usually credits within 1 minute — refresh if balance is unchanged.');
    setStep(null);
    onBalanceChange?.();
  };

  const depositArbUsdc = async () => {
    if (!walletAddress) {
      setError('Link your wallet first (gear icon).');
      return;
    }
    if (!bridgeConfig) {
      setError('Bridge config not loaded — refresh and try again.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    setTxUrl(null);
    const baseline = hlUsdc;
    try {
      setStep('Checking Arbitrum USDC and ETH for gas…');
      await validateBridgeDepositPreflight({
        walletAddress,
        amountUsdc: Number(amount),
        bridgeConfig,
      });
      setStep('Confirm network switch and USDC transfer in your EVM wallet…');
      const result = await runBridgeDeposit({
        walletAddress,
        amountUsdc: Number(amount),
        bridgeConfig,
      });
      setTxUrl(result.explorerUrl);
      setStep('Waiting for Hyperliquid to credit your account…');
      await pollHlCredit(baseline);
      await loadArbBalance();
    } catch (e) {
      setError(formatBridgeDepositError(e, bridgeConfig) || e.message || 'Deposit failed');
      setStep(null);
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(label);
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch {
      setCopyFeedback('Copy failed');
    }
  };

  if (!walletAddress) return null;

  const tabs = [
    { id: 'checklist', label: 'Checklist' },
    { id: 'arbitrum', label: 'USDC (Arbitrum)' },
    { id: 'sol', label: 'SOL (Phantom)' },
    { id: 'phantom', label: 'Phantom setup' },
    { id: 'usdc', label: 'Get USDC' },
  ];

  return (
    <section className={`hl-funding-guide${embedded ? ' hl-funding-guide-embedded' : ''}`}>
      {!embedded && (
        <h3>{singleAsset ? 'Fund for live trading' : 'Fund Hyperliquid for live trading'}</h3>
      )}

      <p className="hl-funding-linked">
        Linked account: <strong>{shortAddr(walletAddress)}</strong>
        {hlUsdc != null && (
          <>
            {' '}
            · Perps USDC: <strong>{fmtUsd(hlUsdc)}</strong>
          </>
        )}
        {spotUsdc != null && Number(spotUsdc) > 0 && (
          <>
            {' '}
            · Spot USDC: <strong>{fmtUsd(spotUsdc)}</strong>
          </>
        )}
        {spotSol != null && Number(spotSol) > 0 && (
          <>
            {' '}
            · Spot SOL: <strong>{Number(spotSol).toFixed(4)}</strong>
          </>
        )}
      </p>

      <div className="hl-funding-tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={tab === t.id ? 'active' : ''}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'checklist' && (
        <div className="hl-funding-pane">
          <ol className="hl-funding-steps">
            {singleAsset ? (
              <>
                <li>
                  <strong>Link an EVM wallet</strong> ({EVM_WALLET_EXAMPLES}) via <strong>HL Wallet</strong> (top
                  right). Use the <strong>Ethereum / 0x account</strong> — not Phantom&apos;s Solana address.
                </li>
                <li>
                  <strong>First deposit on Hyperliquid</strong> (min ~${min} USDC). Use the Arbitrum tab below.
                  Hyperliquid blocks signatures until your linked <code>0x</code> address has received at least one
                  deposit.
                </li>
                <li>
                  <strong>Approve agent</strong> — in HL Wallet, section <strong>Enable live trading</strong> (one
                  wallet signature). Required before live orders work.
                </li>
                <li>
                  <strong>Hold USDC on Hyperliquid</strong> for margin and commission. Unified accounts use spot
                  USDC directly — perps line may show $0 even when funds are tradable.
                </li>
                <li>
                  <strong>Set slippage</strong> in HL Wallet, then place a trade on the Single Asset page.
                </li>
              </>
            ) : (
              <>
                <li>
                  <strong>Link an EVM wallet</strong> ({EVM_WALLET_EXAMPLES}) via the wallet button. Use the{' '}
                  <strong>Ethereum / 0x account</strong> — not Phantom&apos;s Solana address.
                </li>
                <li>
                  <strong>First deposit on Hyperliquid</strong> (min ~${min} USDC or equivalent). Hyperliquid blocks
                  EIP-712 signatures — builder fee approval, agent approval, live trades — until your linked{' '}
                  <code>0x</code> address has received at least one deposit. Error: &quot;Must deposit before performing
                  actions&quot;.
                </li>
                <li>
                  <strong>Hold withdrawable USDC</strong> on Hyperliquid for perps margin, signal payments, and
                  commission. Perps on our site use USDC — not SOL directly.
                </li>
                <li>
                  <strong>Approve builder fee</strong> (one EIP-712 signature in your EVM wallet) when prompted.
                </li>
                <li>
                  <strong>Trade</strong> — manual trades sign each order in your wallet; oBot signs agent approval once
                  then runs server-side.
                </li>
              </>
            )}
          </ol>

          {needsFirstDeposit && !(Number(spotUsdc) > 0) && (
            <p className="hl-fund-warn">
              Your HL perps balance is below ${min}. Complete step 2 below, wait ~1 minute, then retry signatures.
            </p>
          )}
          {Number(spotUsdc) > 0.01 && unifiedAccount && (
            <p className="hl-fund-success-inline">
              Unified account active — <strong>{fmtUsd(spotUsdc)}</strong> is already tradable for perps. No Move
              step needed.
            </p>
          )}
          {Number(spotUsdc) > 0.01 && !unifiedAccount && (
            <div className="hl-fund-spot-box">
              <p className="hl-fund-warn">
                You have <strong>{fmtUsd(spotUsdc)}</strong> USDC on <strong>spot</strong>. Live trading uses{' '}
                <strong>perps</strong> USDC only — move it here (one wallet signature).
              </p>
              <button
                type="button"
                className="primary-btn"
                disabled={busy || !walletAddress}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  setSuccess(null);
                  try {
                    const amountUsd = Math.floor(Number(spotUsdc) * 100) / 100;
                    const result = await moveUsdcSpotToPerpsWithBrowserWallet({
                      walletAddress,
                      amountUsd,
                      apiBase: API_BASE,
                      authHeaders,
                      toPerp: true,
                    });
                    const perpsNow = result?.balances?.perpsUsdc;
                    setHlUsdc(perpsNow ?? result?.withdrawableUsdc ?? null);
                    setSpotUsdc(result?.balances?.spotUsdc ?? 0);
                    setSpotSol(result?.balances?.spotSol ?? null);
                    setSuccess(
                      `Moved ${fmtUsd(amountUsd)} USDC from spot to perps` +
                        (perpsNow != null ? ` — tradable now ${fmtUsd(perpsNow)}.` : '.')
                    );
                    await loadHlBalance();
                    onBalanceChange?.(perpsNow ?? amountUsd);
                  } catch (e) {
                    setError(e.response?.data?.error || e.message);
                    await loadHlBalance();
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? 'Signing…' : `Move ${fmtUsd(spotUsdc)} to trading`}
              </button>
            </div>
          )}
          {accountActive && (
            <p className="hl-fund-success-inline">
              First-deposit requirement appears satisfied ({fmtUsd(hlUsdc)} perps USDC). You can retry builder
              approval or live trading.
            </p>
          )}

          <p className="hl-funding-foot">
            Fastest path: tab <strong>USDC (Arbitrum)</strong>. Easier if you already hold SOL: tab{' '}
            <strong>SOL (Phantom)</strong>, then convert on Omni when ready (no Hyperliquid website required for
            moving USDC to trading).
          </p>
        </div>
      )}

      {tab === 'arbitrum' && (
        <div className="hl-funding-pane">
          <p className="hl-fund-intro">
            Send <strong>USDC on Arbitrum</strong> from your linked EVM wallet. One confirmation — credits in ~1
            minute. Works with {EVM_WALLET_EXAMPLES}. Minimum <strong>${min} USDC</strong>.
          </p>

          <div className="hl-fund-balances">
            <div>
              <span className="hl-fund-bal-label">On Hyperliquid</span>
              <strong>{hlUsdc != null ? fmtUsd(hlUsdc) : '…'}</strong>
            </div>
            <div>
              <span className="hl-fund-bal-label">USDC in wallet (Arbitrum)</span>
              <strong>{arbUsdc != null ? fmtUsd(arbUsdc) : '…'}</strong>
            </div>
          </div>

          <div className="hl-fund-form">
            <label>
              Deposit amount (USDC)
              <input
                type="number"
                min={min}
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={busy}
              />
            </label>
            <button type="button" className="primary-btn" disabled={busy} onClick={depositArbUsdc}>
              {busy ? 'Processing…' : 'Deposit USDC to Hyperliquid'}
            </button>
          </div>

          {step && <p className="hl-fund-step">{step}</p>}
          {txUrl && (
            <p className="hl-fund-tx">
              <a href={txUrl} target="_blank" rel="noopener noreferrer">
                View on Arbiscan
              </a>
            </p>
          )}
          {error && <p className="hl-fund-error">{error}</p>}
          {success && <p className="hl-fund-success">{success}</p>}

          <p className="hl-fund-foot">
            Need USDC on Arbitrum first? See the{' '}
            <button type="button" className="hl-funding-link-btn" onClick={() => setTab('usdc')}>
              Get USDC
            </button>{' '}
            tab. You also need a small amount of ETH on Arbitrum for gas.
          </p>
        </div>
      )}

      {tab === 'sol' && (
        <div className="hl-funding-pane">
          <p className="hl-fund-intro">
            Send <strong>native SOL on Solana</strong> from Phantom (or any Solana wallet). It credits your{' '}
            <strong>same linked Hyperliquid account</strong> ({shortAddr(walletAddress)}). Minimum{' '}
            <strong>0.12 SOL</strong>. Do <strong>not</strong> send USDC on Solana — it will fail.
          </p>

          <ol className="hl-funding-steps compact">
            <li>Link your <strong>Phantom Ethereum / 0x</strong> account on our site first (gear icon).</li>
            <li>In Phantom, switch to your <strong>Solana</strong> account and send ≥0.12 SOL to the address below.</li>
            <li>Wait ~1 minute — USDC usually appears on Hyperliquid <strong>spot</strong> (Omni balance panel).</li>
            <li>
              On Single Asset trading, click <strong>Move to trading</strong>. Phantom switches to your{' '}
              <strong>Ethereum</strong> tab to sign — <strong>no ETH balance needed</strong> (free signature only).
            </li>
            <li>After perps shows USDC, live trading and approvals work on Omni.</li>
          </ol>

          {solLoading && <p className="hl-fund-step">Loading your Solana deposit address…</p>}
          {solError && <p className="hl-fund-error">{solError}</p>}

          {solDeposit?.depositAddress && (
            <div className="hl-funding-sol-box">
              <span className="hl-fund-bal-label">Your Solana deposit address (Unit / Hyperliquid)</span>
              <code className="hl-funding-address">{solDeposit.depositAddress}</code>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => copyText(solDeposit.depositAddress, 'sol')}
              >
                {copyFeedback === 'sol' ? 'Copied!' : 'Copy address'}
              </button>
              <p className="hl-fund-foot">{solDeposit.minDepositNote}</p>
            </div>
          )}

          <button
            type="button"
            className="secondary-btn hl-funding-refresh"
            onClick={loadSolAddress}
            disabled={solLoading}
          >
            Refresh deposit address
          </button>
        </div>
      )}

      {tab === 'phantom' && (
        <div className="hl-funding-pane">
          <p className="hl-fund-intro">
            Phantom has <strong>two separate accounts</strong>. Hyperliquid trading on our site uses only the{' '}
            <strong>Ethereum (EVM)</strong> side.
          </p>

          <div className="hl-funding-phantom-grid">
            <div className="hl-funding-phantom-card">
              <h4>Ethereum account (0x…)</h4>
              <ul>
                <li>Link this address on our site</li>
                <li>Sign EIP-712 (builder fee, agent, payments)</li>
                <li>Sign each manual live trade</li>
                <li>Deposit USDC on Arbitrum (Fund tab)</li>
              </ul>
            </div>
            <div className="hl-funding-phantom-card">
              <h4>Solana account (abc…)</h4>
              <ul>
                <li>Optional — fund via SOL deposit only</li>
                <li>Send SOL to Unit address (SOL tab)</li>
                <li>Does not sign Hyperliquid trades</li>
                <li>After deposit, swap SOL → USDC on HL spot</li>
              </ul>
            </div>
          </div>

          <ol className="hl-funding-steps compact">
            <li>Open Phantom → switch to <strong>Ethereum</strong> → copy your <strong>0x</strong> address.</li>
            <li>On our site, gear icon → select <strong>Phantom (Ethereum)</strong> → Connect.</li>
            <li>For funding with SOL, switch Phantom to <strong>Solana</strong> and use the SOL tab address.</li>
            <li>Always keep the same Hyperliquid account: both paths credit {shortAddr(walletAddress)}.</li>
          </ol>
        </div>
      )}

      {tab === 'usdc' && (
        <div className="hl-funding-pane">
          <p className="hl-fund-intro">
            Live perps need <strong>USDC on Hyperliquid</strong>. The direct route is USDC on{' '}
            <strong>Arbitrum</strong> in your linked EVM wallet, then one-click deposit on the Arbitrum tab.
          </p>

          <h4 className="hl-funding-subhead">If you start with USD / bank / card</h4>
          <p className="hl-fund-foot">
            Fiat → crypto on-ramp is planned for a later release (card/bank via a licensed partner). For now, use
            any exchange or wallet app you already trust, then move USDC to Arbitrum.
          </p>

          <h4 className="hl-funding-subhead">Common paths today</h4>
          <ul className="hl-funding-bullets">
            <li>
              <strong>Coinbase / Kraken / Binance.US</strong> — buy USDC, withdraw to your{' '}
              <strong>0x address on Arbitrum</strong> (select Arbitrum network, not Ethereum mainnet).
            </li>
            <li>
              <strong>Phantom (Ethereum)</strong> — use built-in buy/swap to get USDC on Arbitrum, or bridge from
              another chain inside Phantom.
            </li>
            <li>
              <strong>MetaMask / Rabby</strong> — buy or bridge USDC to Arbitrum via the wallet&apos;s swap/bridge
              features or an exchange withdrawal.
            </li>
            <li>
              <strong>Already hold SOL</strong> — use the SOL tab, then sell for USDC on Hyperliquid spot (no
              Arbitrum USDC needed upfront).
            </li>
          </ul>

          <p className="hl-fund-foot">
            After USDC is on Arbitrum in wallet {shortAddr(walletAddress)}, use{' '}
            <button type="button" className="hl-funding-link-btn" onClick={() => setTab('arbitrum')}>
              USDC (Arbitrum)
            </button>{' '}
            to deposit to Hyperliquid. Keep ~$1–2 of ETH on Arbitrum for gas.
          </p>
        </div>
      )}
    </section>
  );
}
